import { X } from 'lucide-react'
import { useState } from 'react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useDepartments, type FlatDepartment ,
  useCreatePosition,
  useUpdatePosition,
  type PositionWithDetails,
  type CreatePositionInput,
  type UpdatePositionInput,
} from '@/hooks'
import { cn } from '@/lib/utils'

interface PositionFormProps {
  position?: PositionWithDetails
  onClose: () => void
  onSuccess?: () => void
}

export function PositionForm({ position, onClose, onSuccess }: PositionFormProps) {
  const isEdit = !!position
  const createMutation = useCreatePosition()
  const updateMutation = useUpdatePosition()

  const { data: departments } = useDepartments()

  const [formData, setFormData] = useState({
    name: position?.name ?? '',
    code: position?.code ?? '',
    departmentId: position?.departmentId ?? '',
    level: position?.level ?? '',
    description: position?.description ?? '',
    sortOrder: position?.sortOrder ?? 0,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const validate = () => {
    const newErrors: Record<string, string> = {}
    if (!formData.name.trim()) {newErrors.name = '请输入职位名称'}
    if (!formData.code.trim()) {newErrors.code = '请输入职位编码'}
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) {return}

    if (isEdit) {
      const data: UpdatePositionInput = {
        name: formData.name,
        code: formData.code,
        departmentId: formData.departmentId || null,
        level: formData.level ? parseInt(String(formData.level)) : undefined,
        description: formData.description || null,
        sortOrder: formData.sortOrder,
      }
      updateMutation.mutate(
        { id: position.id, data },
        { onSuccess: () => { onSuccess?.(); onClose() } }
      )
    } else {
      const data: CreatePositionInput = {
        name: formData.name,
        code: formData.code,
        departmentId: formData.departmentId || undefined,
        level: formData.level ? parseInt(String(formData.level)) : undefined,
        description: formData.description || undefined,
        sortOrder: formData.sortOrder,
      }
      createMutation.mutate(data, { onSuccess: () => { onSuccess?.(); onClose() } })
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending
  const mutationError = createMutation.error || updateMutation.error

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-lg bg-background shadow-lg">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold">{isEdit ? '编辑职位' : '新建职位'}</h2>
          <button onClick={onClose} className="rounded-md p-1 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium">
                职位名称 <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="请输入职位名称"
                className={cn(
                  'w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary',
                  errors.name && 'border-destructive'
                )}
              />
              {errors.name && <p className="mt-1 text-sm text-destructive">{errors.name}</p>}
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium">
                职位编码 <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="请输入职位编码"
                className={cn(
                  'w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary',
                  errors.code && 'border-destructive'
                )}
              />
              {errors.code && <p className="mt-1 text-sm text-destructive">{errors.code}</p>}
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium">所属部门</label>
              <Select
                value={formData.departmentId || 'none'}
                onValueChange={(value) => setFormData({ ...formData, departmentId: value === 'none' ? '' : value })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="无" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">无</SelectItem>
                  {departments?.map((dept: FlatDepartment) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium">级别 (1-10)</label>
              <input
                type="number"
                value={formData.level}
                onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                min={1}
                max={10}
                placeholder="请输入级别"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium">职位描述</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="请输入职位描述"
                rows={3}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium">排序</label>
              <input
                type="number"
                value={formData.sortOrder}
                onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                min={0}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {mutationError && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {mutationError.message}
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button type="button" onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium hover:bg-muted">
              取消
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isPending ? '保存中...' : isEdit ? '保存' : '创建'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
